import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useTripContext } from './TripDetailPage';
import { venuesApi } from '../../api/venues.api';
import type { Activity, Venue, VenueCategory } from '../../types';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { toast } from '../../components/ui/Toast';
import EmptyState from '../../components/ui/EmptyState';
import { isAlreadyGone } from '../../api/client';
import VenueForm from '../../components/venues/VenueForm';
import AddToItineraryModal from '../../components/venues/AddToItineraryModal';
import { type AiPersistedAction } from '../../components/ai/AiChatPanel';
import { buildGoogleMapsUrl } from '../../utils/googleMaps';
import { useAiDataVersion, useAiPanel, useAiPanelState } from '../../components/ai/AiPanelContext';
import {
  Utensils, Coffee, Landmark, Eye, Building2,
  TreePine, ShoppingBag, Sparkles, Train, MapPin, Waves, Mountain, SquareParking,
  Plus, ExternalLink, Map as MapIcon, Trash2, CheckCircle2, CalendarDays, Check,
  Icon, type LucideIcon,
} from '../../components/ui/Icon';
import { DayPickerMenu } from '../../components/trips/itinerary/DayPickerMenu';
import { itineraryApi } from '../../api/itinerary.api';
import { venueActivityPayload } from '../../utils/venueScheduling';
import { dayNumbersByVenue, dayNumbersOfVenue, dropVenueStops, venueSectionsByDay } from '../../utils/venueStops';

const CAT_META: Record<VenueCategory, {
  Icon: LucideIcon;
  color: string;
  bg: string;
  accent: string;
  pill: string;
}> = {
  RESTAURANT:    { Icon: Utensils,    color: 'text-orange-600', bg: 'bg-orange-50',  accent: '#ea580c', pill: 'bg-orange-100 text-orange-700' },
  CAFE:          { Icon: Coffee,      color: 'text-amber-600',  bg: 'bg-amber-50',   accent: '#d97706', pill: 'bg-amber-100 text-amber-700'  },
  ATTRACTION:    { Icon: Landmark,    color: 'text-violet-600', bg: 'bg-violet-50',  accent: '#7c3aed', pill: 'bg-violet-100 text-violet-700' },
  VIEWPOINT:     { Icon: Eye,         color: 'text-sky-600',    bg: 'bg-sky-50',     accent: '#0284c7', pill: 'bg-sky-100 text-sky-700'      },
  NEIGHBORHOOD:  { Icon: Building2,   color: 'text-indigo-600', bg: 'bg-indigo-50',  accent: '#4f46e5', pill: 'bg-indigo-100 text-indigo-700' },
  PARK:          { Icon: TreePine,    color: 'text-green-600',  bg: 'bg-green-50',   accent: '#16a34a', pill: 'bg-green-100 text-green-700'  },
  SHOP:          { Icon: ShoppingBag, color: 'text-pink-600',   bg: 'bg-pink-50',    accent: '#db2777', pill: 'bg-pink-100 text-pink-700'    },
  EXPERIENCE:    { Icon: Sparkles,    color: 'text-teal-600',   bg: 'bg-teal-50',    accent: '#0d9488', pill: 'bg-teal-100 text-teal-700'    },
  TRANSPORT_HUB: { Icon: Train,       color: 'text-slate-500',  bg: 'bg-slate-100',  accent: '#64748b', pill: 'bg-slate-200 text-slate-600'  },
  BEACH:         { Icon: Waves,         color: 'text-cyan-600',  bg: 'bg-cyan-50',    accent: '#0891b2', pill: 'bg-cyan-100 text-cyan-700'    },
  NATURE:        { Icon: Mountain,      color: 'text-lime-600',  bg: 'bg-lime-50',    accent: '#65a30d', pill: 'bg-lime-100 text-lime-700'    },
  PARKING:       { Icon: SquareParking, color: 'text-blue-600',  bg: 'bg-blue-50',    accent: '#2563eb', pill: 'bg-blue-100 text-blue-700'    },
  OTHER:         { Icon: MapPin,        color: 'text-stone-500', bg: 'bg-stone-100',  accent: '#78716c', pill: 'bg-stone-200 text-stone-600'  },
};
// keep Icon key for backward compat — used via destructure alias below

const ALL_CATEGORIES = Object.keys(CAT_META) as VenueCategory[];

export default function VenuesTab() {
  const { t } = useTranslation();
  const { trip, days, setDays } = useTripContext();
  const [venues, setVenues]         = useState<Venue[]>([]);
  const [loading, setLoading]       = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editVenue, setEditVenue]   = useState<Venue | null>(null);
  const [deleteVenueId, setDeleteVenueId] = useState<string | null>(null);
  const [addToItineraryVenue, setAddToItineraryVenue] = useState<Venue | null>(null);
  // Scheduling saved places one modal at a time was the slow part of building a
  // trip: a dozen places meant a dozen modals. Selection turns that into one.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dayMenuOpen, setDayMenuOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  useAiPanel({ context: 'places', onAction: handleAiAction });
  // The docked panel narrows the content on desktop; drop to 2 columns while
  // it's open so cards don't get cramped (lg:grid-cols-3 only applies at ≥lg,
  // where the panel is always docked/pushing).
  const { collapsed: aiCollapsed } = useAiPanelState();
  // Positive selection: empty = no category filter (every category shows).
  const [selectedCats, setSelectedCats] = useState<Set<VenueCategory>>(new Set());
  const [visitedFilter, setVisitedFilter] = useState<'all' | 'pending' | 'visited'>('all');
  const [dayFilter, setDayFilter] = useState<'all' | 'scheduled' | 'unscheduled'>('all');
  const [search, setSearch] = useState('');

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  /**
   * Schedule every selected place on one day.
   *
   * One create per place rather than a batch endpoint: `createActivity` is what
   * links the stop to its venue and carries the coords already paid for, and a
   * handful of places is a handful of requests. They go together so a slow one
   * doesn't stagger the list.
   */
  async function scheduleSelected(dayId: string) {
    const chosen = venues.filter(v => selectedIds.has(v.id));
    if (chosen.length === 0) return;
    setDayMenuOpen(false);
    setScheduling(true);
    try {
      const created = await Promise.all(
        chosen.map(v => itineraryApi.createActivity(trip.id, dayId, venueActivityPayload(v))
          .then(r => r.activity)),
      );
      setDays(prev => prev.map(d => (d.id === dayId
        ? { ...d, activities: [...d.activities, ...created] }
        : d)));
      const n = days.find(d => d.id === dayId)?.dayNumber;
      toast(t('venues.bulk.scheduled', { count: created.length, n }), 'success');
      setSelectedIds(new Set());
    } catch {
      toast(t('itinerary.toast.failed'), 'error');
    } finally {
      setScheduling(false);
    }
  }

  const venueTodays = useMemo(() => dayNumbersByVenue(days, venues), [days, venues]);

  // ADR-0028: re-read when an accepted card changed a place.
  const aiPlacesVersion = useAiDataVersion('places');
  useEffect(() => {
    venuesApi.list(trip.id)
      .then(setVenues)
      .catch(() => toast(t('venues.toast.loadFailed'), 'error'))
      .finally(() => setLoading(false));
  }, [trip.id, aiPlacesVersion]);

  async function handleToggleVisited(venue: Venue) {
    const updated = await venuesApi.toggleVisited(trip.id, venue.id, !venue.visited);
    setVenues(v => v.map(x => x.id === venue.id ? updated : x));
  }

  async function handleDelete(venueId: string) {
    try {
      await venuesApi.delete(trip.id, venueId);
    } catch (e) {
      if (!isAlreadyGone(e)) { toast(t('common.error'), 'error'); return; }
    }
    setVenues(v => v.filter(x => x.id !== venueId));
    // The server took the stops with it; the page state has to follow or the
    // itinerary keeps painting cards for a place that no longer exists.
    setDays(prev => dropVenueStops(prev, venueId));
    toast(t('venues.toast.deleted'), 'success');
  }

  function handleAiAction(actions: AiPersistedAction[]) {
    for (const action of actions) {
      if (action.type === 'venue_created') {
        setVenues(v => [...v, action.data]);
      }
      if (action.type === 'venue_updated') {
        setVenues(v => v.map(x => (x.id === action.data.id ? action.data : x)));
      }
      if (action.type === 'venues_deleted') {
        const gone = new Set(action.venueIds);
        setVenues(v => v.filter(x => !gone.has(x.id)));
      }
    }
  }

  function handleSaved(venue: Venue) {
    setVenues(v => editVenue
      ? v.map(x => x.id === venue.id ? venue : x)
      : [...v, venue]);
    setCreateOpen(false);
    setEditVenue(null);
  }

  const totalVisited = venues.filter(v => v.visited).length;
  const progress = venues.length > 0 ? Math.round((totalVisited / venues.length) * 100) : 0;

  const q = search.trim().toLowerCase();
  const matchesVisited = (v: Venue) => visitedFilter === 'all' || (visitedFilter === 'visited' ? v.visited : !v.visited);
  const matchesSearch  = (v: Venue) => !q || v.name.toLowerCase().includes(q) || (v.zone ?? '').toLowerCase().includes(q) || (v.notes ?? '').toLowerCase().includes(q);

  const matchesCategory = (v: Venue) => selectedCats.size === 0 || selectedCats.has(v.category);

  const isScheduled = (v: Venue) => (venueTodays.get(v.id)?.length ?? 0) > 0;
  const matchesDay = (v: Venue) => dayFilter === 'all' || (dayFilter === 'scheduled' ? isScheduled(v) : !isScheduled(v));

  const visible = venues.filter(v => matchesCategory(v) && matchesVisited(v) && matchesDay(v) && matchesSearch(v));
  const sections = venueSectionsByDay(days, visible);

  // Chip counts track the search + visited filters (but not the category
  // toggle itself), so they reflect what the user is actually searching for
  // instead of the static per-category totals.
  const countBase = venues.filter(v => matchesVisited(v) && matchesDay(v) && matchesSearch(v));
  const allCount = countBase.length;
  const catCounts = ALL_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = countBase.filter(v => v.category === cat).length;
    return acc;
  }, {} as Record<VenueCategory, number>);

  // Keep the chip set stable (based on all venues) so it doesn't reflow as the
  // user types — the counts going to 0 already signal a non-match.
  const usedCategories = ALL_CATEGORIES.filter(cat => venues.some(v => v.category === cat));

  function toggleCat(cat: VenueCategory) {
    setSelectedCats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      // Picking every category is the same as picking none — collapse back to
      // "All" so the bar never shows a filter that filters nothing.
      return next.size === usedCategories.length ? new Set() : next;
    });
  }

  if (loading) return <LoadingScreen />;

  return (
    <>
      {venues.length === 0 ? (
        <div className="px-4 md:px-8 py-6 md:py-8 w-full animate-fade-in">
          <EmptyState
            icon={MapPin}
            title={t('venues.empty.title')}
            body={t('venues.empty.body')}
            action={{ label: t('venues.empty.addFirst'), icon: Plus, onClick: () => setCreateOpen(true) }}
          />
          <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={t('venues.addPlace')}>
            <VenueForm tripId={trip.id} tripVenues={venues} onSaved={handleSaved} onCancel={() => setCreateOpen(false)} />
          </Modal>
        </div>
      ) : (
        <div className="flex flex-col h-full overflow-hidden">

      {/* ── Top bar ───────────────────────────────────────────────────────── */}
      <div className="shrink-0 px-4 md:px-8 pt-4 md:pt-6 pb-0 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">

        {/* Row 1: title + action buttons */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="min-w-0">
              <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-0.5">
                {trip.destination.toUpperCase()} · {venues.length} {t('venues.placesCount', { count: venues.length })}
              </p>
              <h2
                className="font-serif italic font-semibold text-2xl md:text-3xl text-slate-900 dark:text-slate-100"
                style={{  }}
              >
                {t('venues.title')}
              </h2>
              <div className="flex items-center gap-3 mt-1">
                <div className="w-28 md:w-36 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-teal-500 rounded-full transition-all duration-700"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="font-mono text-[10px] text-slate-400">
                  <span className="font-semibold text-slate-600 dark:text-slate-300">{totalVisited}</span>
                  {' '}/ {venues.length}
                </span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <Link
            to="../map"
            relative="path"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:border-teal-300 hover:text-teal-600 dark:hover:border-teal-700 dark:hover:text-teal-400 transition-all shrink-0"
          >
            <Icon as={MapIcon} size={16} />
            <span className="hidden sm:inline">{t('venues.mapView')}</span>
          </Link>
          <button onClick={() => setCreateOpen(true)} className="btn-primary gap-1.5 shrink-0">
            <Icon as={Plus} size={16} />
            <span className="hidden sm:inline">{t('venues.addPlace')}</span>
            <span className="sm:hidden">{t('common.add')}</span>
          </button>
        </div>

        {/* Row 2: search + visited toggle */}
        <div className="flex items-center gap-2 pb-3">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('common.search')}
              className="w-full pl-7 pr-3 py-1.5 text-xs bg-slate-100 dark:bg-slate-800 border border-transparent focus:border-teal-400 focus:bg-white dark:focus:bg-slate-900 rounded-full outline-none transition-all text-slate-800 dark:text-slate-200 placeholder:text-slate-400"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            )}
          </div>

          {/* Visited filter */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-full p-0.5 gap-0.5 shrink-0">
            {(['all', 'pending', 'visited'] as const).map(f => (
              <button
                key={f}
                onClick={() => setVisitedFilter(f)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                  visitedFilter === f
                    ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                {f === 'all' ? t('venues.filter.all') : f === 'pending' ? t('venues.filter.toVisit') : t('venues.filter.visited')}
              </button>
            ))}
          </div>

          {/* Day filter */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-full p-0.5 gap-0.5 shrink-0">
            {(['all', 'scheduled', 'unscheduled'] as const).map(f => (
              <button
                key={f}
                onClick={() => setDayFilter(f)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                  dayFilter === f
                    ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                {t(`venues.filter.${f}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Row 3: category chips — single scrolling row on mobile, wrapping on desktop */}
        <div className="flex flex-nowrap md:flex-wrap items-center gap-1.5 pb-3 overflow-x-auto md:overflow-visible scrollbar-none -mx-4 px-4 md:mx-0 md:px-0">
          {/* All: clears the category filter */}
          <button
            onClick={() => setSelectedCats(new Set())}
            title={t('map.showAllCategories')}
            className={`flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
              selectedCats.size === 0
                ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Icon as={MapPin} size={12} />
            {t('venues.filter.all')}
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${selectedCats.size === 0 ? 'bg-white/20 dark:bg-black/20' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'}`}>
              {allCount}
            </span>
          </button>

          {usedCategories.map(cat => {
            const { Icon: CatIcon, pill } = CAT_META[cat];
            const isSelected = selectedCats.has(cat);
            const isShown = selectedCats.size === 0 || isSelected;
            const catLabel = t(`venues.categories.${cat}` as Parameters<typeof t>[0]);
            return (
              <button
                key={cat}
                onClick={() => toggleCat(cat)}
                title={isSelected ? t('map.removeCategoryFilter', { label: catLabel }) : t('map.showOnlyCategory', { label: catLabel })}
                className={`flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                  isShown
                    ? `${pill} hover:opacity-80`
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                <Icon as={CatIcon} size={12} />
                {catLabel}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isShown ? 'bg-white/60' : 'bg-slate-200 dark:bg-slate-700'}`}>
                  {catCounts[cat]}
                </span>
              </button>
            );
          })}
        </div>{/* end category chips */}
      </div>{/* end top bar */}

      {/* ── Cards grid ───────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto px-4 md:px-8 py-4 md:py-6">
        {visible.length === 0 ? (
          <EmptyState size="inline" icon={MapPin} title={t('venues.noMatch')} />
        ) : (
          <div className="space-y-6">
            {sections.map(section => (
              <section key={section.dayNumber ?? 'none'}>
                <h3 className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-2">
                  {section.dayNumber === null ? t('venues.noDaySection') : t('itinerary.dayLabel', { n: section.dayNumber })}
                </h3>
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${aiCollapsed ? 'lg:grid-cols-3' : ''} gap-3 sm:gap-4`}>
                  {section.venues.map(venue => {
                    const { Icon: CatIcon, color, accent, bg, pill } = CAT_META[venue.category];
                    return (
                      <div
                        key={venue.id}
                        onClick={() => (selectedIds.size > 0 ? toggleSelect(venue.id) : setEditVenue(venue))}
                        className={`group relative bg-white dark:bg-slate-900/60 rounded-2xl border overflow-hidden flex flex-col transition-all duration-200 cursor-pointer ${
                          selectedIds.has(venue.id)
                            ? 'border-teal-400 ring-2 ring-teal-400/40 dark:border-teal-600'
                            : venue.visited
                            ? 'border-green-200 dark:border-green-900/60'
                            : 'border-slate-200 hover:border-slate-300 hover:shadow-lg hover:-translate-y-0.5 dark:border-slate-800 dark:hover:border-slate-700'
                        }`}
                      >
                        {/* Once anything is selected the whole card toggles, so this
                            only has to start the selection — hence hidden until hover
                            (or until the selection is already running). */}
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); toggleSelect(venue.id); }}
                          aria-label={t('itinerary.bulk.select')}
                          aria-pressed={selectedIds.has(venue.id)}
                          className={`absolute top-2.5 right-2.5 z-10 w-6 h-6 rounded-lg border flex items-center justify-center transition-all cursor-pointer ${
                            selectedIds.has(venue.id)
                              ? 'bg-teal-500 border-teal-500 text-white'
                              : 'bg-white/90 dark:bg-slate-800/90 border-slate-300 dark:border-slate-600 text-transparent opacity-0 group-hover:opacity-100'
                          }`}
                        >
                          <Icon as={Check} size={13} />
                        </button>
                        {/* Color bar */}
                        <div
                          className="h-1 sm:h-1.5 w-full shrink-0 dark:opacity-70"
                          style={{ backgroundColor: venue.visited ? '#bbf7d0' : accent }}
                        />

                        <div className="p-3 sm:p-4 flex flex-col flex-1 gap-2 sm:gap-2.5">

                          {/* Header: icon + name + edit/delete */}
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
                              <Icon as={CatIcon} size={16} className={color} />
                            </div>
                            <div className="flex-1 min-w-0 pt-0.5">
                              <p className={`text-sm font-bold leading-tight ${venue.visited ? 'line-through text-slate-400' : 'text-slate-900 dark:text-slate-100'}`}>
                                {venue.name}
                              </p>
                              {venue.zone && (
                                <p className="text-xs font-medium mt-0.5 truncate" style={{ color: venue.visited ? '#94a3b8' : accent }}>
                                  {venue.zone}
                                </p>
                              )}
                            </div>
                            {/* Actions */}
                            <div className="flex gap-1 shrink-0">
                              <button
                                onClick={e => { e.stopPropagation(); setDeleteVenueId(venue.id); }}
                                className="w-6 h-6 rounded-lg bg-slate-100 hover:bg-red-100 dark:bg-slate-800 dark:hover:bg-red-900/30 flex items-center justify-center cursor-pointer transition-colors"
                                aria-label={t('common.delete')}
                              >
                                <Icon as={Trash2} size={12} className="text-slate-500 hover:text-red-500" />
                              </button>
                            </div>
                          </div>

                          {/* Category + price pills */}
                          <div className="flex flex-wrap gap-1.5">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${pill}`}>
                              <Icon as={CatIcon} size={12} />
                              {venue.customCategory ?? t(`ai.category.${venue.category}` as Parameters<typeof t>[0])}
                            </span>
                            {venue.priceInfo && (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                                {venue.priceInfo}
                              </span>
                            )}
                            {venue.source === 'IMPORT' && (
                              <span
                                title={t('common.fromDocumentHint')}
                                className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400"
                              >
                                {t('common.fromDocument')}
                              </span>
                            )}
                          </div>

                          {/* Notes */}
                          {venue.notes && (
                            <p className="text-xs text-slate-400 italic leading-relaxed line-clamp-1 sm:line-clamp-2 break-words">
                              {venue.notes}
                            </p>
                          )}

                          {/* Footer */}
                          <div className="flex items-center justify-between mt-auto pt-2 sm:pt-2.5 border-t border-slate-50 dark:border-slate-800">
                            <div className="flex items-center gap-3">
                              {(() => {
                                const href = venue.mapsUrl ?? buildGoogleMapsUrl({
                                  name: venue.name,
                                  placeId: venue.placeId,
                                  latitude: venue.latitude,
                                  longitude: venue.longitude,
                                  address: venue.address,
                                  zone: venue.zone,
                                  destination: trip.destination,
                                });
                                return href && (
                                  <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={e => e.stopPropagation()}
                                    className="flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-teal-600 transition-colors"
                                  >
                                    <Icon as={MapIcon} size={12} />
                                    {t('venues.maps')}
                                  </a>
                                );
                              })()}
                              {venue.bookingUrl && (
                                <a
                                  href={venue.bookingUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={e => e.stopPropagation()}
                                  className="flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-indigo-600 transition-colors"
                                >
                                  Book
                                  <Icon as={ExternalLink} size={12} />
                                </a>
                              )}
                            </div>

                            {(() => {
                              const scheduledDays = venueTodays.get(venue.id);
                              if (scheduledDays && scheduledDays.length > 0) {
                                const label = scheduledDays.length === 1
                                  ? t('venues.day_one', { days: scheduledDays[0] })
                                  : t('venues.day_other', { days: `${scheduledDays.slice(0, 2).join(', ')}${scheduledDays.length > 2 ? '…' : ''}` });
                                return (
                                  <button
                                    onClick={e => { e.stopPropagation(); setAddToItineraryVenue(venue); }}
                                    className="flex items-center gap-1 text-xs font-medium text-indigo-500 hover:text-indigo-700 transition-colors cursor-pointer"
                                    title={t('venues.addToItinerary')}
                                  >
                                    <Icon as={CalendarDays} size={14} />
                                    {label}
                                  </button>
                                );
                              }
                              return (
                                <button
                                  onClick={e => { e.stopPropagation(); setAddToItineraryVenue(venue); }}
                                  className="flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-indigo-600 transition-colors cursor-pointer"
                                  title={t('venues.addToItinerary')}
                                >
                                  <CalendarDays className="w-3.5 h-3.5" strokeWidth={2} />
                                  {t('venues.addToDay')}
                                </button>
                              );
                            })()}

                            <button
                              onClick={e => { e.stopPropagation(); handleToggleVisited(venue); }}
                              className="flex items-center gap-1.5 text-xs font-semibold transition-all cursor-pointer rounded-lg px-2 py-1 hover:bg-slate-50 dark:hover:bg-slate-800"
                            >
                              {venue.visited ? (
                                <>
                                  <Icon as={CheckCircle2} size={14} className="text-green-500" />
                                  <span className="text-green-600">{t('venues.visited')}</span>
                                </>
                              ) : (
                                <>
                                  <Icon as={CheckCircle2} size={14} className="text-slate-200" />
                                  <span className="text-slate-400">{t('venues.markVisited')}</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      <Modal open={!!deleteVenueId} onClose={() => setDeleteVenueId(null)} title={t('venues.delete.title')}>
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">{t('venues.delete.body')}</p>
          {/* Deleting a place takes its stops with it. This tab has the days in
              context already, so the warning names them rather than asking the
              user to go and check. */}
          {deleteVenueId && dayNumbersOfVenue(days, deleteVenueId).length > 0 && (
            <p className="text-sm font-semibold text-red-600 dark:text-red-400">
              {t('venues.delete.withStops', {
                count: dayNumbersOfVenue(days, deleteVenueId).length,
                days: dayNumbersOfVenue(days, deleteVenueId).join(', '),
              })}
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => { handleDelete(deleteVenueId!); setDeleteVenueId(null); }}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold py-2 rounded-xl transition-colors cursor-pointer"
            >
              {t('venues.delete.confirm')}
            </button>
            <button onClick={() => setDeleteVenueId(null)} className="flex-1 btn-secondary text-sm">{t('common.cancel')}</button>
          </div>
        </div>
      </Modal>
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={t('venues.addPlace')}>
        <VenueForm tripId={trip.id} tripVenues={venues} onSaved={handleSaved} onCancel={() => setCreateOpen(false)} />
      </Modal>
      <Modal open={!!editVenue} onClose={() => setEditVenue(null)} title={t('venues.editPlace')}>
        {editVenue && (
          <VenueForm tripId={trip.id} tripVenues={venues} existing={editVenue} onSaved={handleSaved} onCancel={() => setEditVenue(null)} />
        )}
      </Modal>
      <Modal open={!!addToItineraryVenue} onClose={() => setAddToItineraryVenue(null)} title={t('venues.addToItinerary')}>
        {addToItineraryVenue && (
          <AddToItineraryModal
            venue={addToItineraryVenue}
            days={days}
            tripId={trip.id}
            onSaved={({ added, removed }) => {
              setDays(prev => prev.map(d => {
                const toAdd = added.filter(i => i.dayId === d.id).map(i => i.activity);
                const removedIds = new Set(removed.filter(i => i.dayId === d.id).map(i => i.activityId));
                if (toAdd.length === 0 && removedIds.size === 0) return d;
                return {
                  ...d,
                  activities: [...d.activities.filter(a => !removedIds.has(a.id)), ...toAdd],
                };
              }));
              setAddToItineraryVenue(null);
            }}
            onCancel={() => setAddToItineraryVenue(null)}
          />
        )}
      </Modal>
        </div>
      )}

      {/* Bulk selection action bar — same shape and place as the itinerary's, so
          selecting things means the same thing in both tabs. */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-36 sm:bottom-24 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-slate-900 dark:bg-slate-800 text-white rounded-2xl px-5 py-3 shadow-2xl whitespace-nowrap animate-fade-in">
          <span className="text-sm font-medium">{t('itinerary.bulk.selected', { count: selectedIds.size })}</span>
          <div className="w-px h-4 bg-white/20" />
          <div className="relative">
            <button
              type="button"
              disabled={scheduling || days.length === 0}
              onClick={() => setDayMenuOpen(o => !o)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors cursor-pointer hover:text-teal-300 disabled:opacity-40"
            >
              {scheduling ? <Spinner size="xs" /> : <Icon as={CalendarDays} size={14} />}
              {t('venues.bulk.addToDay')}
            </button>
            {dayMenuOpen && (
              <DayPickerMenu
                days={days}
                title={t('itinerary.bulk.moveTo')}
                onClose={() => setDayMenuOpen(false)}
                onPick={scheduleSelected}
              />
            )}
          </div>
          <div className="w-px h-4 bg-white/20" />
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="text-slate-400 hover:text-white text-sm transition-colors cursor-pointer"
          >
            {t('itinerary.bulk.cancel')}
          </button>
        </div>
      )}
    </>
  );
}
